import { Button, ManageableItemsList } from '@payaca/ambrosia';
import { MaterialsListPermissions } from '@payaca/permissions/materialsList/materialsList.permissions';
import { StockLocationsPermissions } from '@payaca/permissions/stockLocations/stockLocations.permissions';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState, type JSX } from 'react';

import stockLocationKeys from '@/api/queries/stockLocations/keyFactory';
import useGetStockLocationPurchaseOrders from '@/api/queries/stockLocations/useGetStockLocationPurchaseOrders';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { PurchaseOrdersManageableItemsListTable } from '@/ui/components/purchaseOrdersManageableItemsListTable/PurchaseOrdersManageableItemsListTable';
import { usePaginationState } from '@/ui/pages/projectPage/components/MaterialsPanel/materialsPanelHooks';

import RaiseStockPurchaseOrderSidebar from './RaiseStockPurchaseOrderSidebar';

type Props = {
  stockLocationId: string;
};

const PAGE_SIZE = 20;

const StockLocationPurchaseOrders: FC<Props> = (props: Props): JSX.Element => {
  const { stockLocationId } = props;

  const queryClient = useQueryClient();

  const { pagination, setCurrentPage } = usePaginationState(PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [isRaisingPurchaseOrder, setIsRaisingPurchaseOrder] = useState(false);

  const { purchaseOrders, isLoading } = useGetStockLocationPurchaseOrders(
    stockLocationId,
    { searchTerm: appliedSearchTerm || undefined },
    pagination
  );

  return (
    <ManageableItemsList>
      <ManageableItemsList.HeaderBar
        buttons={
          <PermissionGuard
            renderIfHasPermissions={[
              MaterialsListPermissions.PERSIST_PURCHASE_ORDER,
              StockLocationsPermissions.PERSIST_STOCK_LOCATION,
            ]}
            requireAllPermissions
          >
            <Button size="sm" onClick={() => setIsRaisingPurchaseOrder(true)}>
              Create purchase order
            </Button>
          </PermissionGuard>
        }
      />
      <ManageableItemsList.ActionBar>
        <ManageableItemsList.ActionBar.SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          onChangeTimeout={(value) => {
            setCurrentPage(1);
            setAppliedSearchTerm(value);
          }}
          changeTimeoutMs={250}
        />
      </ManageableItemsList.ActionBar>
      <PurchaseOrdersManageableItemsListTable
        purchaseOrders={purchaseOrders?.items ?? []}
        isLoading={isLoading}
        returnToStockLocationId={stockLocationId}
        includedColumns={[
          'ref',
          'status',
          'supplier',
          'forecast-cost',
          'purchase-cost',
        ]}
        onUpdateSuccess={() =>
          queryClient.invalidateQueries({
            queryKey: stockLocationKeys.detail(stockLocationId),
          })
        }
      />
      <ManageableItemsList.PaginationBar
        type="custom"
        pageSize={PAGE_SIZE}
        currentPage={pagination.offset / pagination.limit + 1}
        totalItems={purchaseOrders?.totalCount ?? 0}
        onPageChange={setCurrentPage}
      />
      <RaiseStockPurchaseOrderSidebar
        stockLocationId={stockLocationId}
        isOpen={isRaisingPurchaseOrder}
        onClose={() => setIsRaisingPurchaseOrder(false)}
      />
    </ManageableItemsList>
  );
};

export default StockLocationPurchaseOrders;
